import {
  AbstractBuildstep,
  type OptionDB,
  ThreeToolsInstance,
} from "@project/emerge";
import { Sides } from "@project/emerge-types";
import filter from "lodash/filter";
import find from "lodash/find";
import groupBy from "lodash/groupBy";
import { Vector2, Vector3 } from "three";
import { EdgeType } from "../enum/EdgeType";
import { EndpointType } from "../enum/EndpointType";
import { InteriorType } from "../enum/InteriorType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { LTProducts } from "../enum/LTProducts";
import { MiterType } from "../enum/MiterType";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import { LTPartType } from "../enum/PartType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { PanelColor } from "../interfaces/PanelColor";
import type { RackRailDefinition } from "../interfaces/RackRailProperties";
import type { RackRailType } from "../interfaces/RackRailType";
import type { TrayMaterialType } from "../interfaces/TrayMaterialType";
import type { UnderlayPanelDefinition } from "../interfaces/UnderlayPanelProperties";
import { DefaultGroupName } from "../structure/Rect";
import { RectPropertiesBySide } from "../structure/RectProperties";
import { rackUnitHeight } from "./FlightCaseBlueprintCalculator";
import {
  getDefaultPanelColor,
  resolveTrayMaterialByStrength,
} from "./interior-helpers/InteriorHelpers";

const underlayWidthByStrength: Record<number, number> = {
  0: 0,
  6.5: 40,
  9: 35,
};

// Helper function to calculate miter type based on endpoint conditions
function getMiterType(startOnPanel: boolean, endOnPanel: boolean): MiterType {
  if (startOnPanel && endOnPanel) {
    return MiterType.BOTH;
  } else if (startOnPanel) {
    return MiterType.START;
  } else if (endOnPanel) {
    return MiterType.END;
  }
  return MiterType.NONE;
}

// Dependency data types for the incremental build pattern
type RackRailDependencyData = {
  partKind: "RackRail";
  edgeIndex: number;
  cutReference: Sides;
  miterType: MiterType;
  partSelection: RackRailType;
  points: [Vector3, Vector3];
  position: Vector3;
  side: Sides;
  orientation: number;
};

type UnderlayPanelDependencyData = {
  partKind: "UnderlayPanel";
  edgeIndex: number;
  cutReference: Sides;
  segmentIndex: number;
  material: TrayMaterialType;
  orientation: number;
  position: Vector3;
  size: Vector2;
  side: Sides;
  cutouts: never[];
  note: string;
  color: PanelColor; // For rendering compatibility
};

type RackRailDependency = RackRailDependencyData | UnderlayPanelDependencyData;

/**
 * The underlay strength under a rack rail. A rail type without a forced
 * strength (`undefined`) uses the interior's strength; `null` means "no
 * underlay" (0), as the stored rail types (e.g. STW6160) rely on; a number
 * forces that strength.
 */
export function rackRailUnderlayStrength(
  baseStrength: number | undefined,
  railType: Pick<RackRailType, "forceUnderlayStrength">,
): number {
  if (railType.forceUnderlayStrength === undefined) {
    return baseStrength ?? 0;
  }
  return railType.forceUnderlayStrength ?? 0;
}

/**
 * Rack19InteriorBuildStep handles interior parts for RACK19 interior type:
 * - Rack rails (19-inch rack mount rails)
 * - Underlay panels
 *
 * This buildstep is used by advanced product types: Case3d
 */
export class Rack19InteriorBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  RackRailDefinition | UnderlayPanelDefinition,
  RackRailDependency
> {
  constructor(private readonly optionDb: OptionDB) {
    super("Rack19InteriorBuildStep");
  }

  private filterRackRailEdges(edges: FlightCaseBlueprint["edges"]): Array<
    FlightCaseBlueprint["edges"][number] & {
      cut: NonNullable<FlightCaseBlueprint["edges"][number]["cut"]>;
      sides: [Sides, ...Sides[]];
    }
  > {
    return filter(
      edges,
      (
        e,
      ): e is FlightCaseBlueprint["edges"][number] & {
        cut: NonNullable<FlightCaseBlueprint["edges"][number]["cut"]>;
        sides: [Sides, ...Sides[]];
      } => {
        if (e.group !== DefaultGroupName) return false;
        if (e.type !== EdgeType.FROMCUT) return false;
        if (e.cut === null) return false;
        if (e.sides.length === 0) return false;
        if (
          e.sides.indexOf(Sides.LEFT) === -1 &&
          e.sides.indexOf(Sides.RIGHT) === -1
        ) {
          return false;
        }
        return true;
      },
    );
  }

  private calculateRackRailPoints(
    edge: FlightCaseBlueprint["edges"][number] & {
      cut: NonNullable<FlightCaseBlueprint["edges"][number]["cut"]>;
    },
    settings: FlightCaseSettings,
    options: {
      rackRailOffsets: Record<string, number>;
      rackRailDisabled: Record<string, boolean>;
      standupRackRailOnTop: boolean;
      alignToProfiles: number;
      underlayStrength: number;
      korpusNormal: Vector3;
    },
  ): { points: [Vector3, Vector3]; neighbourDisabled: [boolean, boolean] } {
    const {
      rackRailOffsets,
      rackRailDisabled,
      standupRackRailOnTop,
      alignToProfiles,
      underlayStrength,
      korpusNormal,
    } = options;
    const neighbourDisabled: [boolean, boolean] = [false, false];
    const points = [edge.start, edge.end].map((endpoint, n) => {
      const isOnCut = endpoint.type === EndpointType.ON_PANEL;
      const rackRailOffset = new Vector3();
      for (const cutName of endpoint.cutsAffectedBy) {
        const cut = find(settings.cuts, { groupName: cutName });
        if (!cut) continue;
        const rectProps = RectPropertiesBySide[cut.reference];
        if (!rectProps) continue;
        const { normalDir } = rectProps;
        if (
          cutName !== edge.cut.groupName &&
          rackRailDisabled?.[cutName] === true
        ) {
          neighbourDisabled[n] = true;
        }
        rackRailOffset.add(
          normalDir.clone().multiplyScalar(-(rackRailOffsets[cutName] || 0)),
        );
      }
      return endpoint.innerPos
        .clone()
        .add(edge.inwards.clone().multiplyScalar(alignToProfiles))
        .add(
          edge.direction.multiplyScalar(
            (n === 0 ? 1 : -1) * (isOnCut ? alignToProfiles : 0),
          ),
        )
        .add(
          standupRackRailOnTop && isOnCut && edge.dir === "y"
            ? new Vector3(0, -rackUnitHeight)
            : new Vector3(),
        )
        .add(rackRailOffset)
        .add(korpusNormal.clone().multiplyScalar(-underlayStrength));
    }) as [Vector3, Vector3];

    if (edge.outerLength === edge.originalEdgeLength) {
      const dir = points[1]
        .clone()
        .sub(points[0])
        .normalize()
        .multiplyScalar(0.5);
      points[0].add(dir);
      points[1].sub(dir);
    }

    return { points, neighbourDisabled };
  }

  private calculateRackRailOrientation(
    edge: FlightCaseBlueprint["edges"][number],
    cutReference: string,
  ): number {
    let orientation =
      edge.orientationOnRect === OrientationOnRect.VERTICAL
        ? ThreeToolsInstance.DEG2RAD(90)
        : 0;
    const isTopLeft =
      +cutReference === Sides.TOP && edge.sides[0] === Sides.LEFT;
    if (isTopLeft) {
      orientation += Math.PI;
    }
    return orientation;
  }

  private adjustPointsForLCase(
    points: [Vector3, Vector3],
    edge: FlightCaseBlueprint["edges"][number],
    standupRackRailOnTop: boolean,
    settings: FlightCaseSettings,
  ): [Vector3, Vector3] {
    const clonedPoints: [Vector3, Vector3] = [
      points[0].clone(),
      points[1].clone(),
    ];
    if (settings.type === LTProducts.LCase && standupRackRailOnTop) {
      const endpoints: [typeof edge.start, typeof edge.end] = [
        edge.start,
        edge.end,
      ];
      for (const e of [0, 1] as const) {
        if (edge.dir === "z") {
          if (endpoints[e].type === EndpointType.ALONG_EDGE) {
            clonedPoints[e].z += 3;
          }
        }
      }
    }
    return clonedPoints;
  }

  private createRackRailDependency(
    edge: FlightCaseBlueprint["edges"][number] & {
      sides: [Sides, ...Sides[]];
    },
    cutReference: string,
    railType: RackRailType,
    miterType: MiterType,
    points: [Vector3, Vector3],
    orientation: number,
  ): RackRailDependencyData {
    const position = points[0].clone().add(points[1]).multiplyScalar(0.5);
    return {
      partKind: "RackRail",
      edgeIndex: edge.index,
      cutReference: +cutReference,
      miterType,
      partSelection: railType,
      points,
      position,
      side: edge.sides[0],
      orientation,
    };
  }

  private adjustVerticalUnderlay(
    p0: Vector3,
    p1: Vector3,
    edge: FlightCaseBlueprint["edges"][number],
    options: {
      points: [Vector3, Vector3];
      neighbourDisabled: [boolean, boolean];
      standupRackRailOnTop: boolean;
      underlayStrength: number;
      e: 0 | 1;
    },
  ): void {
    const {
      points,
      neighbourDisabled,
      standupRackRailOnTop,
      underlayStrength,
      e,
    } = options;
    const endpoints: [typeof edge.start, typeof edge.end] = [
      edge.start,
      edge.end,
    ];
    const pointPair: [Vector3, Vector3] = [p0, p1];
    if (
      edge.dir === "y" &&
      !neighbourDisabled[e] &&
      endpoints[e].type === EndpointType.ON_PANEL &&
      (pointPair[e].y === points[0].y || pointPair[e].y === points[1].y)
    ) {
      if (!standupRackRailOnTop) {
        pointPair[e].y -= underlayWidthByStrength[underlayStrength] || 0;
      }
    }
  }

  private adjustLCaseUnderlay(
    p0: Vector3,
    p1: Vector3,
    edge: FlightCaseBlueprint["edges"][number],
    standupRackRailOnTop: boolean,
    settings: FlightCaseSettings,
    e: 0 | 1,
  ): void {
    if (settings.type === LTProducts.LCase && standupRackRailOnTop) {
      if (edge.dir === "z") {
        const endpoints: [typeof edge.start, typeof edge.end] = [
          edge.start,
          edge.end,
        ];
        const pointPair: [Vector3, Vector3] = [p0, p1];
        if (endpoints[e].type === EndpointType.ALONG_EDGE) {
          pointPair[e].z += 3;
        }
      }
    }
  }

  private adjustUnderlayPoints(
    p0: Vector3,
    p1: Vector3,
    edge: FlightCaseBlueprint["edges"][number],
    options: {
      points: [Vector3, Vector3];
      neighbourDisabled: [boolean, boolean];
      standupRackRailOnTop: boolean;
      underlayStrength: number;
      settings: FlightCaseSettings;
    },
  ): [Vector3, Vector3] {
    const {
      points,
      neighbourDisabled,
      standupRackRailOnTop,
      underlayStrength,
      settings,
    } = options;
    for (const e of [0, 1] as const) {
      this.adjustVerticalUnderlay(p0, p1, edge, {
        points,
        neighbourDisabled,
        standupRackRailOnTop,
        underlayStrength,
        e,
      });
      this.adjustLCaseUnderlay(p0, p1, edge, standupRackRailOnTop, settings, e);
    }
    return [p0, p1];
  }

  private createUnderlayPanelDependency(
    edge: FlightCaseBlueprint["edges"][number] & {
      sides: [Sides, ...Sides[]];
    },
    cutReference: string,
    segmentIndex: number,
    p0: Vector3,
    p1: Vector3,
    underlayStrength: number,
    korpusNormal: Vector3,
  ): UnderlayPanelDependencyData {
    const underlayWidth = underlayWidthByStrength[underlayStrength] || 0;
    return {
      partKind: "UnderlayPanel",
      edgeIndex: edge.index,
      cutReference: +cutReference,
      segmentIndex,
      material: resolveTrayMaterialByStrength(this.optionDb, underlayStrength),
      note: "Unterlegholz",
      orientation:
        edge.orientationOnRect === OrientationOnRect.HORIZONTAL ? 90 : 0,
      position: p0
        .clone()
        .lerp(p1, 0.5)
        .add(edge.inwards.clone().multiplyScalar(underlayWidth / 2))
        .add(korpusNormal.clone().multiplyScalar(underlayStrength / 2)),
      size: new Vector2(underlayWidth, p0.distanceTo(p1)),
      side: edge.sides[0],
      cutouts: [],
      color: getDefaultPanelColor(),
    };
  }

  private processEdge(
    edge: FlightCaseBlueprint["edges"][number] & {
      cut: NonNullable<FlightCaseBlueprint["edges"][number]["cut"]>;
      sides: [Sides, ...Sides[]];
    },
    cutReference: string,
    settings: FlightCaseSettings,
    options: {
      rackRailOffsets: Record<string, number>;
      rackRailDisabled: Record<string, boolean>;
      standupRackRailOnTop: boolean;
      rackRails: RackRailType;
      standupRackRails: RackRailType;
      dependencies: Map<string, RackRailDependency>;
    },
  ): void {
    const {
      rackRailOffsets,
      rackRailDisabled,
      standupRackRailOnTop,
      rackRails,
      standupRackRails,
      dependencies,
    } = options;
    const isStandup = standupRackRailOnTop && edge.dir !== "y";
    const korpusNormal = RectPropertiesBySide[edge.sides[0]].normalDir;
    if (rackRailDisabled?.[edge.cut.groupName]) {
      return;
    }

    const baseUnderlayStrength =
      settings.interiorOptions.type === InteriorType.RACK19
        ? settings.interiorOptions.underlayStrength
        : undefined;
    const underlayStrength = rackRailUnderlayStrength(
      baseUnderlayStrength,
      isStandup ? standupRackRails : rackRails,
    );
    const railType = isStandup ? standupRackRails : rackRails;
    const alignToProfiles =
      settings.exteriorOptions.profiles.height +
      settings.exteriorOptions.profiles.extra / 2;

    const { points, neighbourDisabled } = this.calculateRackRailPoints(
      edge,
      settings,
      {
        rackRailOffsets,
        rackRailDisabled,
        standupRackRailOnTop,
        alignToProfiles,
        underlayStrength,
        korpusNormal,
      },
    );

    const miterType = standupRackRailOnTop
      ? MiterType.NONE
      : getMiterType(
          edge.start.type === EndpointType.ON_PANEL &&
            neighbourDisabled[0] === false,
          edge.end.type === EndpointType.ON_PANEL &&
            neighbourDisabled[1] === false,
        );

    const clonedPoints = this.adjustPointsForLCase(
      points,
      edge,
      standupRackRailOnTop,
      settings,
    );

    const orientation = this.calculateRackRailOrientation(edge, cutReference);
    const rackRailId = `rackrail_${edge.index}_${cutReference}_${edge.sides[0]}`;
    const rackRailDep = this.createRackRailDependency(
      edge,
      cutReference,
      railType,
      miterType,
      clonedPoints,
      orientation,
    );
    dependencies.set(rackRailId, rackRailDep);

    for (let n = 0; n < edge.cuttingConnectors.length + 1; n++) {
      const x1 =
        n === 0 ? points[0][edge.dir] : edge.getCutPosition(n, false)[edge.dir];
      const x2 =
        n === edge.connectors.length
          ? points[1][edge.dir]
          : edge.getCutPosition(n + 1, true)[edge.dir];
      const p0 = points[0].clone();
      p0[edge.dir] = x1;
      const p1 = points[1].clone();
      p1[edge.dir] = x2;

      const [adjustedP0, adjustedP1] = this.adjustUnderlayPoints(p0, p1, edge, {
        points,
        neighbourDisabled,
        standupRackRailOnTop,
        underlayStrength,
        settings,
      });

      if (underlayStrength !== 0) {
        const underlayId = `underlay_${edge.index}_${cutReference}_${edge.sides[0]}_${n}`;
        const underlayDep = this.createUnderlayPanelDependency(
          edge,
          cutReference,
          n,
          adjustedP0,
          adjustedP1,
          underlayStrength,
          korpusNormal,
        );
        dependencies.set(underlayId, underlayDep);
      }
    }
  }

  // Main entry point - collects all dependency data
  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, RackRailDependency> {
    const dependencies = new Map<string, RackRailDependency>();

    if (settings.interiorOptions.type !== InteriorType.RACK19) {
      return dependencies;
    }

    const {
      rackRailOffsets,
      rackRailDisabled = {},
      standupRackRailOnTop,
      rackRails,
      standupRackRails,
    } = settings.interiorOptions;

    const filtered = this.filterRackRailEdges(blueprint.edges);
    const edgesByGroupAndReference = groupBy(filtered, "cutReference");

    for (const cutReference in edgesByGroupAndReference) {
      if (Object.hasOwn(edgesByGroupAndReference, cutReference)) {
        const edges = edgesByGroupAndReference[cutReference];
        if (!edges) continue;
        for (const edge of edges) {
          this.processEdge(edge, cutReference, settings, {
            rackRailOffsets,
            rackRailDisabled,
            standupRackRailOnTop,
            rackRails,
            standupRackRails,
            dependencies,
          });
        }
      }
    }

    return dependencies;
  }

  public createPart(
    partId: string,
    dependencies: RackRailDependency,
  ): RackRailDefinition | UnderlayPanelDefinition {
    if (dependencies.partKind === "RackRail") {
      const dep = dependencies;
      const properties: RackRailDefinition = {
        id: partId,
        group: DefaultGroupName,
        buildstepName: this.buildstepName,
        type: LTPartType.RackRail,
        cutReference: dep.cutReference,
        miterType: dep.miterType,
        partSelection: dep.partSelection,
        points: [dep.points[0].clone(), dep.points[1].clone()],
        position: dep.position.clone(),
        orientation: dep.orientation,
        rotation: this.rotateOntoSide(dep.side, 0),
      };
      return properties;
    } else {
      const dep = dependencies;
      // Extract strength from material (like TrayPanelDefinition does)
      const strength = dep.material.strength;
      const properties: UnderlayPanelDefinition = {
        id: partId,
        group: DefaultGroupName,
        buildstepName: this.buildstepName,
        type: LTPartType.Panel,
        cutouts: dep.cutouts,
        material: dep.material,
        orientation: dep.orientation,
        position: dep.position.clone(),
        size: dep.size.clone(),
        side: dep.side,
        strength: strength, // Required by PanelRenderer
        color: dep.color, // Required by PanelRenderer
        rotation: this.rotateOntoSide(dep.side, 0),
      };
      return properties;
    }
  }
}
